import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Dumbbell, Zap, Building2, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStore } from "@/lib/auth";
import { getLoginUrl } from "@/lib/auth";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Interval = "monthly" | "yearly";

const plans = [
  {
    tier: "free" as const,
    name: "Free",
    icon: Dumbbell,
    description: "Get started with flight tracking",
    price: { monthly: 0, yearly: 0 },
    features: [
      "1 fare search per 24 hours",
      "Basic FlexWeek (7-day window)",
      "Coach AI: 3 conversations/month",
      "1 active route alert",
    ],
    cta: "Current Plan",
    highlight: false,
  },
  {
    tier: "pro" as const,
    name: "Pro",
    icon: Zap,
    description: "For serious travelers",
    price: { monthly: 799, yearly: 5900 },
    features: [
      "Unlimited fare searches",
      "FlexWeek: Full 30-day flexible grid",
      "Coach AI: Unlimited conversations",
      "Email alerts: 10 active routes",
      "Price history graphs (30-day)",
      "Priority search queue",
    ],
    cta: "Upgrade to Pro",
    highlight: true,
  },
  {
    tier: "business" as const,
    name: "Business",
    icon: Building2,
    description: "For teams & power users",
    price: { monthly: 2499, yearly: 19900 },
    features: [
      "Everything in Pro",
      "Multi-city & complex itineraries",
      "Team sharing (up to 5 seats)",
      "API access for travel managers",
      "90-day price history",
      "Custom alert rules",
      "Dedicated support",
    ],
    cta: "Upgrade to Business",
    highlight: false,
  },
];

function formatPrice(cents: number): string {
  return (cents / 100).toFixed(2).replace(/\.00$/, "");
}

export default function PricingPage() {
  const [interval, setInterval] = useState<Interval>("monthly");
  const [loading, setLoading] = useState<string | null>(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);
  const canceled = searchParams.get("canceled") === "true";

  async function handleUpgrade(tier: "pro" | "business") {
    if (!isAuthenticated) {
      window.location.href = getLoginUrl();
      return;
    }

    setLoading(tier);
    try {
      const res = await api.post("/billing/checkout", { tier, interval });
      window.location.href = res.data.url;
    } catch (err: any) {
      console.error("Checkout error:", err);
      alert(err.response?.data?.error || "Failed to start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  }

  const yearlySavings = Math.round((1 - 5900 / (799 * 12)) * 100);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50">
        <div className="container mx-auto max-w-6xl px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5 text-primary" />
            <span className="font-bold text-lg">TripMuscle</span>
          </button>
          {isAuthenticated ? (
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
              Dashboard
            </Button>
          ) : (
            <Button size="sm" onClick={() => (window.location.href = getLoginUrl())}>
              Sign In
            </Button>
          )}
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-4 py-16">
        {canceled && (
          <div className="mb-8 rounded-lg bg-amber-50 border border-amber-200 p-4 text-center text-sm text-amber-700">
            Checkout was canceled. No charges were made. Ready when you are!
          </div>
        )}

        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-3">
            Find Better Flights,{" "}
            <span className="text-primary">Pay Less</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            TripMuscle monitors flight prices so you don't have to.
            Upgrade for unlimited searches, AI coaching, and smarter alerts.
          </p>
        </div>

        {/* Interval toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-lg bg-muted p-1">
            <button
              onClick={() => setInterval("monthly")}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                interval === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("yearly")}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                interval === "yearly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Yearly
              <span className="ml-1.5 text-xs text-emerald-600 font-semibold">
                Save {yearlySavings}%
              </span>
            </button>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const price = plan.price[interval];
            const isCurrentFree = plan.tier === "free";

            return (
              <Card
                key={plan.tier}
                className={cn(
                  "relative flex flex-col p-6 rounded-xl border transition-all",
                  plan.highlight
                    ? "border-primary shadow-lg shadow-primary/10 scale-[1.02]"
                    : "border-border"
                )}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs font-semibold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}

                <div className="mb-4">
                  <plan.icon className={cn("h-8 w-8 mb-3", plan.highlight ? "text-primary" : "text-muted-foreground")} />
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="font-mono text-4xl font-bold">
                      ${formatPrice(price)}
                    </span>
                    {!isCurrentFree && (
                      <span className="text-muted-foreground text-sm">
                        /{interval === "monthly" ? "mo" : "yr"}
                      </span>
                    )}
                  </div>
                  {!isCurrentFree && interval === "yearly" && (
                    <p className="text-xs text-emerald-600 mt-1">
                      <span className="font-mono">${formatPrice(Math.round(price / 12))}/mo</span> billed annually
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrentFree ? (
                  <Button variant="outline" className="w-full" disabled>
                    {isAuthenticated ? "Current Plan" : "Get Started Free"}
                  </Button>
                ) : (
                  <Button
                    className="w-full group"
                    variant={plan.highlight ? "default" : "outline"}
                    disabled={loading === plan.tier}
                    onClick={() => handleUpgrade(plan.tier as "pro" | "business")}
                  >
                    {loading === plan.tier ? (
                      "Redirecting..."
                    ) : (
                      <>
                        {plan.cta}
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>

        {/* FAQ / Trust */}
        <div className="mt-16 text-center text-sm text-muted-foreground space-y-2">
          <p className="inline-flex items-center justify-center gap-1.5">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Secure payments powered by Stripe. Cancel anytime.
          </p>
          <p>
            Questions?{" "}
            <a href="mailto:support@wade.technology" className="text-primary hover:underline">
              support@wade.technology
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
